import { Link, useNavigate, useLocation } from 'react-router-dom';
// useCommandPalette import dropped 2026-05-26 with the pill removal.
import { useTenant } from '@state/useTenant';
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { useCdxIdentity, initialsOf, friendlyDisplayName } from '@state/useCdxIdentity';
import { routeSlugOf } from '@pages/flight-deck/analytics';
import { programScopedTarget } from '@state/programScoped';

/**
 * Header — editorial topbar (paper aesthetic, dark ink-950 background).
 *
 * Pattern (from Claude Design "Data Portal — Alignment" bundle, shell.jsx):
 *   [brand-mark "C"] [Clari*Dynamics*] [sep] [AHP · Alignment Health Plan]
 *   [sep] [campaign chip strip · active programs] [+ Activate]
 *   [sep] [search Cmd+K] [refresh] [help] [avatar]
 *
 * Per spec §3 ClariCommand: serif italic brand, mono uppercase meta, tabular figures.
 */
export function Header() {
  // const { open } = useCommandPalette(); — pill removed 2026-05-26
  const tenant = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const tShort =
    tenant.tenantId === 'alignment' ? 'AHP' : tenant.tenantId === 'claridyne' ? 'CHP' : 'CDX';
  const tName = tenant.tenantName;

  // Per-recipient identity from the signed URL → cookie → bootAuth → window.CDX_IDENTITY
  const identity = useCdxIdentity();
  const userName = identity ? friendlyDisplayName(identity.displayName) : 'Guest';
  const userInitials = identity ? initialsOf(identity.displayName) : '?';
  const userRole = identity ? `Reviewer · ${tShort}` : `Analyst · ${tShort}`;

  // Active campaigns drive the chip strip — sourced from the shared Setup store
  // so toggles on /setup propagate live to the header (and the "Active in header" KPI).
  // Board-only campaigns stay off the strip: they live on the Flight Deck board
  // only (clear board_only in the Setup Summary to bring one back).
  const activeCampaigns = useSetupCampaigns((s) => s.campaigns.filter((c) => c.active && !c.boardOnly));
  const selectedCampaignId = useSetupCampaigns((s) => s.selectedCampaignId);
  const selectCampaign = useSetupCampaigns((s) => s.selectCampaign);

  return (
    <header className="topbar" role="banner">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexShrink: 0 }}>
        <Link to="/" aria-label="ClariDynamics home" className="brand-mark" style={{ textDecoration: 'none' }}>
          C
        </Link>
        <Link to="/" className="brand-name" style={{ whiteSpace: 'nowrap', textDecoration: 'none', color: 'inherit' }}>
          Clari<em>Dynamics</em>
        </Link>
      </div>

      <span className="sep" style={{ flexShrink: 0 }} />

      <div className="tb-meta" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        <span>
          <b>{tShort}</b> · {tName}
        </span>
      </div>

      <span className="sep" style={{ flexShrink: 0 }} />

      {activeCampaigns.length > 0 ? (
        <div
          style={{
            display: 'inline-flex',
            gap: 0,
            border: '1px solid var(--ink-700)',
            borderRadius: 2,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {activeCampaigns.map((c, i, arr) => {
            const label = (c.nickname && c.nickname.trim()) || c.code;
            const isSelected = c.id === selectedCampaignId;
            const title = isSelected
              ? `${c.code} · ${c.measure.split('·')[0]?.trim()} · cohort ${c.cohort.toLocaleString()} · in focus`
              : `${c.code} · ${c.measure.split('·')[0]?.trim()} · cohort ${c.cohort.toLocaleString()} · click to focus`;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => {
                  selectCampaign(c.id);
                  const target = programScopedTarget(location.pathname, routeSlugOf(c));
                  if (target) navigate(target);
                }}
                title={title}
                style={{
                  padding: '5px 11px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  fontWeight: isSelected ? 700 : 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: isSelected ? 'var(--paper-3, #e5e0d3)' : 'var(--ink-900)',
                  color: isSelected ? 'var(--ink-950, #0a1220)' : 'var(--ink-200)',
                  border: 'none',
                  borderRight: i < arr.length - 1 ? '1px solid var(--ink-700)' : 'none',
                  cursor: 'pointer',
                  transition: 'background 120ms ease, color 120ms ease',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/setup')}
          title="Activate at least one campaign in Setup"
          style={{
            padding: '5px 11px',
            border: '1px dashed var(--ink-700)',
            borderRadius: 2,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            background: 'transparent',
            color: 'var(--ink-300)',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          + Activate campaign
        </button>
      )}

      <button
        type="button"
        onClick={() => navigate('/setup')}
        title="Open Campaign Setup"
        style={{
          padding: '4px 8px',
          border: '1px solid var(--ink-700)',
          borderRadius: 2,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          fontWeight: 500,
          background: 'transparent',
          color: 'var(--ink-300)',
          flexShrink: 0,
          marginLeft: 6,
          cursor: 'pointer',
        }}
        aria-label="Open Campaign Setup"
      >
        ⚙
      </button>

      {/* Command-palette pill removed per William 2026-05-26 — was the
          "Navigate · search · actions ⌘K" button between Campaign Setup ⚙
          and the refresh/help icons. Cmd+K keyboard shortcut still works
          via useCommandPalette wherever else it's bound. */}
      <span style={{ marginLeft: 'auto' }} />

      <div className="tb-avatar" title={identity ? `${userName} · ${identity.email}` : undefined}>
        <span className="av-circle">{userInitials}</span>
        <div>
          <div className="nm">{userName}</div>
          <div className="ro">{userRole}</div>
        </div>
      </div>
    </header>
  );
}
